import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    useReplaceProductsInMicrostoreColumnTemplatesMutation,
    MicrostoreItemType,
    useGetMicrostoreColumnTemplatesWithItemQuery,
} from 'data/__generated__';

import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { MicrostoreItemSelect, MicrostoreItemSelected } from 'components/Select/MicrostoreItemSelect';

export const ReplaceProductInTemplates = ({
    initialItemToRemove,
    onSuccess,
    showItemToRemoveSelect = true,
}: {
    initialItemToRemove?: MicrostoreItemSelected;
    onSuccess?: () => void;
    showItemToRemoveSelect?: boolean;
}) => {
    const [itemToRemove, setItemToRemove] = useState<MicrostoreItemSelected | null>(initialItemToRemove || null);
    const [itemToAdd, setItemToAdd] = useState<MicrostoreItemSelected | null>(null);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
    const [replaceProductsInTemplates, { loading: replaceLoading }] =
        useReplaceProductsInMicrostoreColumnTemplatesMutation();

    const { data: templatesData } = useGetMicrostoreColumnTemplatesWithItemQuery({
        skip: !itemToRemove,
        variables: { itemId: itemToRemove?._id || '', itemType: itemToRemove?.type || MicrostoreItemType.Product },
    });

    const templates = templatesData?.microstoreColumnTemplatesWithItem || [];
    const templatesCount = templates.length;
    const columnsCount = templates.reduce((sum, t) => sum + t.microstoreColumns.length, 0);

    useEffect(() => {
        setSelectedTemplateIds(templates.map((t) => t._id));
    }, [templates.length]);

    const selectedTemplates = templates.filter((t) => selectedTemplateIds.includes(t._id));
    const selectedLocationsCount = selectedTemplates.reduce((sum, t) => sum + t.templateLocationCount, 0);

    const toggleTemplateSelection = (templateId: string) => {
        setSelectedTemplateIds((prev) => {
            if (prev.includes(templateId)) {
                return prev.filter((id) => id !== templateId);
            } else {
                return [...prev, templateId];
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedTemplateIds.length === templates.length) {
            setSelectedTemplateIds([]);
        } else {
            setSelectedTemplateIds(templates.map((t) => t._id));
        }
    };

    const handleSubmit = async () => {
        if (!itemToRemove) {
            toast.error('Veuillez sélectionner un élément à remplacer');
            return;
        }

        if (!itemToAdd) {
            toast.error('Veuillez sélectionner un nouvel élément');
            return;
        }

        if (selectedTemplateIds.length === 0) {
            toast.error('Veuillez sélectionner au moins un template');
            return;
        }

        const input = {
            itemToRemove: {
                itemId: itemToRemove._id,
                itemType: itemToRemove.type,
            },
            itemToAdd: {
                itemId: itemToAdd._id,
                itemType: itemToAdd.type,
            },
            microstoreColumnTemplateIds: selectedTemplateIds,
        };

        const { data } = await replaceProductsInTemplates({ variables: { input } });

        if (data?.replaceProductsInMicrostoreColumnTemplatesMutation.errors?.length) {
            data.replaceProductsInMicrostoreColumnTemplatesMutation.errors.forEach(
                (error: string | null, index: number) => {
                    if (error) {
                        toast.error(<span key={index}>{error}</span>, { autoClose: false });
                    }
                },
            );
            return;
        }

        const itemType = itemToRemove.type === MicrostoreItemType.Product ? 'produits' : 'gammes';
        toast.success(
            `Les ${itemType} ont été remplacés avec succès dans les ${selectedLocationsCount} emplacements !`,
        );

        onSuccess?.();
    };

    return (
        <Container>
            {showItemToRemoveSelect && (
                <MicrostoreItemSelect
                    label="Élément à remplacer :"
                    placeholder="Sélectionner le produit ou la gamme à remplacer"
                    value={itemToRemove?._id || null}
                    onChange={setItemToRemove}
                    isClearable
                    UsedInTemplates
                />
            )}

            {itemToRemove && (
                <TemplatesContainer>
                    <TemplatesHeader>
                        <TemplatesTitle>
                            Templates impactés ({templatesCount} template
                            {templatesCount > 1 ? 's' : ''}, {columnsCount} colonne
                            {columnsCount > 1 ? 's' : ''}) :
                        </TemplatesTitle>
                        <SelectAllButton onClick={toggleSelectAll}>
                            {selectedTemplateIds.length === templates.length
                                ? 'Tout désélectionner'
                                : 'Tout sélectionner'}
                        </SelectAllButton>
                    </TemplatesHeader>

                    <TemplatesList>
                        {templates.map((template) => (
                            <TemplateItem key={template._id}>
                                <TemplateHeader>
                                    <CheckboxLabel>
                                        <input
                                            type="checkbox"
                                            checked={selectedTemplateIds.includes(template._id)}
                                            onChange={() => toggleTemplateSelection(template._id)}
                                        />
                                        <TemplateName>
                                            {template.name} ({template.templateLocationCount} emplacement
                                            {template.templateLocationCount > 1 ? 's' : ''})
                                        </TemplateName>
                                    </CheckboxLabel>
                                </TemplateHeader>
                                <ColumnsList>
                                    {template.microstoreColumns.map((column) => (
                                        <ColumnItem key={column._id}>
                                            • {column.site.name} - {column.name}
                                        </ColumnItem>
                                    ))}
                                </ColumnsList>
                            </TemplateItem>
                        ))}
                    </TemplatesList>
                </TemplatesContainer>
            )}

            <MicrostoreItemSelect
                label="Nouvel élément :"
                placeholder="Sélectionner le nouveau produit ou la nouvelle gamme"
                value={itemToAdd?._id || null}
                onChange={setItemToAdd}
                isClearable
            />

            <TotemPrimaryLoadingButton
                loading={replaceLoading}
                useIcon={false}
                label={`Remplacer dans les ${selectedTemplateIds.length} template${selectedTemplateIds.length > 1 ? 's' : ''} sélectionné${selectedTemplateIds.length > 1 ? 's' : ''}`}
                onClick={handleSubmit}
                disabled={!itemToRemove || !itemToAdd || selectedTemplateIds.length === 0}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const TemplatesContainer = styled.div`
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    padding: 15px;
    max-height: 300px;
    overflow-y: auto;
`;

const TemplatesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TemplatesTitle = styled.div`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;

const SelectAllButton = styled.button`
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    background: ${({ theme }) => theme.ctaPrimaryBorderColor};
`;

const TemplatesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const TemplateItem = styled.div`
    padding: 5px;
    border-radius: 6px;
`;

const TemplateHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    flex: 1;

    input {
        cursor: pointer;
        width: 20px;
        height: 20px;
    }
`;

const TemplateName = styled.div`
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const ColumnsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 10px;
`;

const ColumnItem = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;
